import * as React from 'react';
import {
  Button,
  List,
  ListItem,
  Modal,
  ModalVariant,
  Wizard,
  WizardHeader,
  WizardStep,
} from '@patternfly/react-core';
import ExternalLinkSquareAltIcon from '@patternfly/react-icons/dist/esm/icons/external-link-square-alt-icon';
import { SiteCheck, SiteConfig } from '../../config/bootstrap';
import { startCollection } from './startCollection';

const logoutUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout';

interface CheckWizardProps {
  check: SiteCheck;
  site: SiteConfig;
  isOpen: boolean;
  startIndex: number;
  onClose: () => void;
  onStepChange: (step: number) => void;
}

export const CheckWizard: React.FunctionComponent<CheckWizardProps> = ({
  check,
  site,
  isOpen,
  startIndex,
  onClose,
  onStepChange,
}) => {
  const [hasLoggedOut, setHasLoggedOut] = React.useState(false);
  const [oauthError, setOauthError] = React.useState<string | null>(null);
  const steps = check.wizardSteps || [];

  const handleOauth = async (variant: string) => {
    setOauthError(null);
    try {
      const url = await startCollection(check.taskDefId, variant);
      window.location.href = url;
    } catch (error: any) {
      setOauthError(error?.message || 'Unable to start sign-in');
    }
  };

  return (
    <Modal isOpen={isOpen} variant={ModalVariant.medium}>
      <Wizard
        height={steps.length > 5 ? 600 : 500}
        onClose={onClose}
        title={check.productLabel}
        startIndex={startIndex}
        onStepChange={(_event, currentStep) => onStepChange(Number(currentStep.id))}
        isVisitRequired
        header={
          <WizardHeader
            onClose={onClose}
            title={check.title}
            description="Follow these steps to perform a security assessment of your environment."
          />
        }
      >
        {steps.map((step) => {
          if (step.kind === 'intro') {
            return (
              <WizardStep key={step.id} name={step.name} id={step.id}>
                <List>
                  <ListItem>Follow the link in the next step to be redirected to Microsoft.</ListItem>
                  <ListItem>You will be prompted to review and accept permissions required for data collection.</ListItem>
                  <ListItem>
                    With the exception of write permissions used to revoke access, each of the permissions requested are
                    read-only and allow {site.productName} to collect data.
                  </ListItem>
                </List>
              </WizardStep>
            );
          }
          if (step.kind === 'oauth') {
            return (
              <WizardStep key={step.id} name={step.name} id={step.id} footer={{ isNextDisabled: true }}>
                <p>Click the button below to register the {site.productName} application in your environment.</p>
                <br />
                <Button
                  variant="primary"
                  icon={<ExternalLinkSquareAltIcon />}
                  iconPosition="end"
                  onClick={() => handleOauth(step.variant || 'register')}
                >
                  {step.buttonLabel || site.productName}
                </Button>
                {oauthError && <p style={{ marginTop: '0.5rem' }}>{oauthError}</p>}
              </WizardStep>
            );
          }
          if (step.kind === 'registered') {
            return (
              <WizardStep key={step.id} name={step.name} id={step.id}>
                <p>The {site.productName} application has been successfully registered in your environment.</p>
                <br />
                <p>You can now proceed to the next step.</p>
              </WizardStep>
            );
          }
          if (step.kind === 'logout') {
            return (
              <WizardStep key={step.id} name={step.name} id={step.id} footer={{ isNextDisabled: !hasLoggedOut }}>
                <p>To revoke session cookies used during setup, please logout from Microsoft before proceeding:</p>
                <br />
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Button
                    variant="primary"
                    icon={<ExternalLinkSquareAltIcon />}
                    iconPosition="end"
                    onClick={() => {
                      window.open(logoutUrl, 'Microsoft Logout', 'width=500,height=600,left=100,top=100');
                      setHasLoggedOut(true);
                    }}
                  >
                    Logout from Microsoft
                  </Button>
                  <p>(Opens in a new window)</p>
                </div>
              </WizardStep>
            );
          }
          return (
            <WizardStep
              key={step.id}
              name={step.name}
              id={step.id}
              footer={{ nextButtonText: 'Finish', onNext: onClose }}
            >
              <b>Success!</b>
              <br />
              <br />
              Thank you for completing the registration process.
              <br />
              <br />
              Data collection will commence shortly.
              <br />
              <br />
              A member of the team will contact you as soon as your report is available.
              <br />
              <br />
              If you need any further assistance please{' '}
              <Button
                variant="primary"
                component="a"
                href={`mailto:${site.supportEmail}?subject=${site.productName} support`}
              >
                Contact Support
              </Button>
            </WizardStep>
          );
        })}
      </Wizard>
    </Modal>
  );
};
